/**
 * Inbox: everything that needs you, from every app. Chats (direct, group, channel, task thread) are one row per
 * conversation, refreshed with the latest line. Actions on a row (5 Nov 2026): reply inline, snooze menu, make a
 * task, hand to someone, approve or decline inline, and select many for done / snooze / dismiss in one go.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { AlarmClock, AtSign, Bell, Check, CheckSquare, ChevronDown, FileQuestion, Hourglass, Inbox as InboxIcon, ListPlus, MessageCircle, Reply, Scale, Siren, Stamp, ThumbsDown, ThumbsUp, UserRound, X, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { api, post } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, Modal, PageHeader } from "../components/ui";
import { Due, PersonPicker, PriorityBadge, SubProgress } from "../components/work";
import type { Person } from "../lib/work";

export interface InboxItem {
  id: string;
  category: string;
  sourceType: string;
  sourceId: string;
  title: string;
  reason: string;
  priority: string;
  dueAt: string | null;
  link: string;
  state: string;
  seenAt: string | null;
  updatedAt: string;
  subtasks?: { total: number; done: number } | null;
}

export const CATS: Array<[string, string]> = [
  ["", "All"], ["chat", "Chats"], ["mention", "Mentions"], ["task", "Tasks"], ["approval", "Approvals"], ["decision", "Decisions"], ["request", "Requests"], ["escalation", "Escalations"], ["waiting", "Waiting on"], ["update", "Updates"],
];

const CAT_STYLE: Record<string, { label: string; icon: LucideIcon; bg: string; fg: string }> = {
  task: { label: "Task", icon: CheckSquare, bg: "bg-brand-50", fg: "text-brand-600" },
  approval: { label: "Approval", icon: Stamp, bg: "bg-violet-50", fg: "text-violet-600" },
  decision: { label: "Decision", icon: Scale, bg: "bg-indigo-50", fg: "text-indigo-600" },
  request: { label: "Request", icon: FileQuestion, bg: "bg-sky-50", fg: "text-sky-600" },
  mention: { label: "Mention", icon: AtSign, bg: "bg-teal-50", fg: "text-teal-600" },
  chat: { label: "Chat", icon: MessageCircle, bg: "bg-emerald-50", fg: "text-emerald-600" },
  escalation: { label: "Escalation", icon: Siren, bg: "bg-red-50", fg: "text-red-600" },
  waiting: { label: "Waiting on you", icon: Hourglass, bg: "bg-amber-50", fg: "text-amber-600" },
  update: { label: "Update", icon: Bell, bg: "bg-slate-100", fg: "text-slate-500" },
};

export const useInbox = (category = "") =>
  useQuery({ queryKey: ["inbox", category], queryFn: () => api<{ items: InboxItem[]; counts: Record<string, number> }>(`/api/inbox${category ? `?category=${category}` : ""}`), refetchInterval: 60_000 });

/** Snooze choices, in Malta time. */
const SNOOZES: Array<[string, () => Date]> = [
  ["Later today (3 h)", () => new Date(Date.now() + 3 * 3600_000)],
  ["Tomorrow 08:00", () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0); return d; }],
  ["Next Monday 08:00", () => { const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); d.setHours(8, 0, 0, 0); return d; }],
];

const canReply = (i: InboxItem) => i.sourceType === "chat_conversation" || i.sourceType === "chat_message" || ((i.category === "mention" || i.category === "chat") && i.sourceType === "work_item");
const canHand = (i: InboxItem) => i.sourceType === "work_item" && (i.category === "task" || i.category === "waiting" || i.category === "escalation");
const canDecide = (i: InboxItem) => i.category === "approval" && (i.sourceType === "approval_request" || i.sourceType === "work_item");

export function InboxPage() {
  const [cat, setCat] = useState("");
  const inbox = useInbox(cat);
  const qc = useQueryClient();
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["inbox"] }); void qc.invalidateQueries({ queryKey: ["chat"] }); };
  const act = useMutation({
    mutationFn: ({ id, action, until }: { id: string; action: string; until?: string }) => post(`/api/inbox/${id}`, { action, until }),
    onSuccess: refresh,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulk = useMutation({
    mutationFn: async ({ action, until }: { action: string; until?: string }) => { for (const id of selected) await post(`/api/inbox/${id}`, { action, until }); },
    onSuccess: () => { setSelected(new Set()); refresh(); },
  });
  const counts = inbox.data?.counts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const items = inbox.data?.items ?? [];
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div>
      <PageHeader title="Inbox" subtitle="Everything that needs you, from every app, in one place." />
      <div className="mb-4 max-w-full overflow-x-auto"><div className="tabs" role="tablist">
        {CATS.map(([k, label]) => {
          const n = k ? counts[k] : total;
          return (
            <button key={k} role="tab" aria-selected={cat === k} className="tab" onClick={() => setCat(k)}>
              {label}{n ? <span className="count num">{n}</span> : null}
            </button>
          );
        })}
      </div></div>
      <ErrorNote error={inbox.error ?? act.error ?? bulk.error} />
      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-sm">
          <span className="font-semibold">{selected.size} selected</span>
          <button className="btn btn-sm" disabled={bulk.isPending} onClick={() => bulk.mutate({ action: "done" })}><Check size={14} /> Done</button>
          <button className="btn btn-sm" disabled={bulk.isPending} onClick={() => bulk.mutate({ action: "snooze", until: SNOOZES[1]![1]().toISOString() })}><AlarmClock size={14} /> Snooze till tomorrow</button>
          <button className="btn btn-sm" disabled={bulk.isPending} onClick={() => bulk.mutate({ action: "dismiss" })}><X size={14} /> Dismiss</button>
          <button className="btn btn-sm ml-auto" onClick={() => setSelected(new Set())}>Clear</button>
          <button className="btn btn-sm" onClick={() => setSelected(new Set(items.map((i) => i.id)))}>Select all {items.length}</button>
        </div>
      )}
      <Card flush bodyClassName="!pb-0">
        {inbox.isLoading ? <Empty>Loading…</Empty> : items.length ? (
          <ul className="divide-y divide-[var(--line)]">
            {items.map((i) => <Row key={i.id} i={i} selected={selected.has(i.id)} onSelect={() => toggle(i.id)} act={(action, until) => act.mutate({ id: i.id, action, until })} onDone={refresh} />)}
          </ul>
        ) : <Empty icon={<InboxIcon size={20} />}>Inbox zero. Nothing needs you right now.</Empty>}
      </Card>
    </div>
  );
}

function Row({ i, selected, onSelect, act, onDone }: { i: InboxItem; selected: boolean; onSelect: () => void; act: (action: string, until?: string) => void; onDone: () => void }) {
  const c = CAT_STYLE[i.category] ?? CAT_STYLE.update!;
  const [reply, setReply] = useState<string | null>(null);
  const [snoozing, setSnoozing] = useState(false);
  const [handing, setHanding] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const send = useMutation({
    mutationFn: async (text: string) => {
      if (i.sourceType === "chat_conversation") return post(`/api/chat/conversations/${i.sourceId}/messages`, { body: text });
      if (i.sourceType === "chat_message") {
        const m = await api<{ conversationId: string }>(`/api/chat/messages/${i.sourceId}`);
        return post(`/api/chat/conversations/${m.conversationId}/messages`, { body: text });
      }
      return post(`/api/work/items/${i.sourceId}/comments`, { body: text });
    },
    onSuccess: () => { setReply(null); act("done"); onDone(); },
    onError: setError,
  });
  const makeTask = useMutation({
    mutationFn: () => post<{ item: { id: string } }>("/api/tasks/quick", { text: i.title, description: `From your inbox (${c.label}): ${i.reason}\n${location.origin}${i.link}` }),
    onSuccess: () => { act("done"); onDone(); },
    onError: setError,
  });
  const decide = useMutation({
    mutationFn: (approve: boolean) => i.sourceType === "approval_request"
      ? post(`/api/approvals/${i.sourceId}/decide`, { decision: approve ? "approve" : "reject", comment: note || undefined })
      : post(`/api/work/items/${i.sourceId}/verify`, { approve, note: note || undefined }),
    onSuccess: () => { setNote(null); onDone(); },
    onError: setError,
  });

  return (
    <li className={clsx("group px-5 py-4 transition-colors hover:bg-[#fafbff]", !i.seenAt && "bg-brand-50/30", selected && "bg-brand-50")}>
      <div className="flex items-start gap-3.5">
        <input type="checkbox" className="mt-2.5 h-4 w-4 shrink-0" checked={selected} onChange={onSelect} aria-label={`Select ${i.title}`} />
        <span className={clsx("mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl", c.bg)}><c.icon size={17} className={c.fg} /></span>
        <Link to={i.link as "/"} className="min-w-0 flex-1" onClick={() => !i.seenAt && act("seen")}>
          <span className="flex flex-wrap items-center gap-2">
            {!i.seenAt && <span className="h-2 w-2 rounded-full bg-brand-500" aria-label="Unread" />}
            <span className={clsx("text-[0.9375rem] text-[var(--text)] group-hover:text-brand-700", i.seenAt ? "font-medium" : "font-bold")}>{i.title}</span>
            <PriorityBadge p={i.priority} />
            <SubProgress s={i.subtasks} />
          </span>
          <span className="muted mt-1 block text-[0.8125rem]">{i.reason}</span>
          <span className="subtle mt-1.5 flex flex-wrap items-center gap-3 text-xs">
            <span className={clsx("font-semibold", c.fg)}>{c.label}</span>
            <Due at={i.dueAt} />
            <span>{fmtDate(i.updatedAt)}</span>
          </span>
        </Link>
        <span className="flex shrink-0 flex-wrap justify-end gap-1 opacity-70 transition-opacity group-hover:opacity-100">
          {canReply(i) && <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Reply" aria-label="Reply" onClick={() => setReply((r) => (r === null ? "" : null))}><Reply size={15} /></button>}
          {canDecide(i) && (
            <>
              <button className="btn btn-ghost btn-sm btn-icon !w-8 text-emerald-700" title="Approve" aria-label="Approve" disabled={decide.isPending} onClick={() => decide.mutate(true)}><ThumbsUp size={15} /></button>
              <button className="btn btn-ghost btn-sm btn-icon !w-8 text-red-700" title="Decline (with a note)" aria-label="Decline" onClick={() => setNote((n) => (n === null ? "" : null))}><ThumbsDown size={15} /></button>
            </>
          )}
          {canHand(i) && <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Hand to someone" aria-label="Hand to someone" onClick={() => setHanding(true)}><UserRound size={15} /></button>}
          <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Make a task from this" aria-label="Make a task" disabled={makeTask.isPending} onClick={() => makeTask.mutate()}><ListPlus size={15} /></button>
          <span className="relative">
            <button className="btn btn-ghost btn-sm !px-1.5" title="Snooze" aria-label="Snooze" aria-expanded={snoozing} onClick={() => setSnoozing((x) => !x)}><AlarmClock size={15} /><ChevronDown size={11} /></button>
            {snoozing && (
              <span className="absolute right-0 z-20 mt-1 flex w-48 flex-col rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1 text-sm shadow-lg" onMouseLeave={() => setSnoozing(false)}>
                {SNOOZES.map(([label, when]) => <button key={label} className="rounded-lg px-3 py-1.5 text-left hover:bg-[#f4f5fb]" onClick={() => { setSnoozing(false); act("snooze", when().toISOString()); }}>{label}</button>)}
                <label className="rounded-lg px-3 py-1.5 text-left hover:bg-[#f4f5fb]">Pick a date…<input type="datetime-local" className="input mt-1 !min-h-0 !py-1 text-xs" onChange={(e) => { if (e.target.value) { setSnoozing(false); act("snooze", new Date(e.target.value).toISOString()); } }} /></label>
              </span>
            )}
          </span>
          {(i.category === "mention" || i.category === "chat" || i.category === "update" || i.category === "escalation") ? (
            <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Done" aria-label="Mark done" onClick={() => act("done")}><Check size={15} /></button>
          ) : (
            <button className="btn btn-ghost btn-sm btn-icon !w-8" title="Dismiss from inbox" aria-label="Dismiss" onClick={() => act("dismiss")}><X size={15} /></button>
          )}
        </span>
      </div>
      <ErrorNote error={error} />
      {reply !== null && (
        <div className="mt-2 flex items-end gap-2 pl-[4.75rem]">
          <textarea className="input min-h-[2.5rem] flex-1" rows={1} autoFocus placeholder={i.sourceType === "work_item" ? "Reply in the task's thread…" : "Reply…"} value={reply} onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (reply.trim()) send.mutate(reply.trim()); } if (e.key === "Escape") setReply(null); }} />
          <button className="btn btn-primary" disabled={!reply.trim() || send.isPending} onClick={() => send.mutate(reply.trim())}>Send</button>
        </div>
      )}
      {note !== null && (
        <div className="mt-2 flex items-end gap-2 pl-[4.75rem]">
          <input className="input flex-1" autoFocus placeholder="Why, or what needs changing" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") decide.mutate(false); if (e.key === "Escape") setNote(null); }} />
          <button className="btn" disabled={decide.isPending} onClick={() => decide.mutate(false)}><ThumbsDown size={14} /> Decline</button>
        </div>
      )}
      {handing && <HandTo i={i} onClose={() => setHanding(false)} onDone={() => { setHanding(false); act("dismiss"); onDone(); }} />}
    </li>
  );
}

function HandTo({ i, onClose, onDone }: { i: InboxItem; onClose: () => void; onDone: () => void }) {
  const [people, setPeople] = useState<Person[]>([]);
  const m = useMutation({
    mutationFn: () => post(`/api/work/items/${i.sourceId}/assign`, { assigneeIds: people.map((p) => p.id), helperIds: [], reviewerIds: [] }),
    onSuccess: onDone,
  });
  return (
    <Modal title={`Hand “${i.title}” to…`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!people.length || m.isPending} onClick={() => m.mutate()}>Hand over</button></>}>
      <ErrorNote error={m.error} />
      <PersonPicker value={people} onChange={setPeople} placeholder="Who takes this?" />
      <p className="muted mt-2 text-xs">They become the assignee and get it in their inbox; it leaves yours.</p>
    </Modal>
  );
}
